import React from 'react';
import { LoremIpsum } from 'lorem-ipsum';
import { sortBy, snakeCase, lowerCase } from 'lodash';
import { FormattedMessage } from 'react-intl';
import { get } from 'lodash';
import { empty } from 'apollo-link';

export const nameForUser = user => user.nick_name || user.user_name;

export const bioForUser = (user, authorized) => {
  const lorem = new LoremIpsum({
    sentencesPerParagraph : {
      max : 8,
      min : 4,
    },
    wordsPerSentence      : {
      max : 16,
      min : 4,
    },
  });
  if (authorized) {
    if (user.is_student) {
      return <FormattedMessage id="sign_up_pages.default_student_bio" />;
    } else {
      return (user.has_team ? user.team.teamDescription : user.bio) || <FormattedMessage id="sign_up_pages.default_bio" />;
    }
  }
  return lorem.generateSentences(2);
};

export const locationForUser = user => {
  if (!user.city) return <span />;
  return (
    <span>
      <span className="fa fa-map-marker " />
      <strong>
        <small>
          {user.city}, {user.province}
        </small>
      </strong>
    </span>
  );
};

const majorityProductLine = user => {
  const lines = [
    { name: 'Insurance', val: user.mix_insurance },
    { name: 'Segmented Funds', val: user.mix_annuities },
    { name: 'Mutual Funds', val: user.mix_mutual_funds },
  ];
  return sortBy(lines, 'val');
};

/**
 * Stable, deterministic hash of a string -> unsigned 32-bit int.
 * Used so a given user always sees the same suggestion across refreshes,
 * but different users get different variants. Not cryptographic.
 */
const stableHash = (str) => {
  const s = String(str || 'anon');
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h) + s.charCodeAt(i);
    h |= 0; // force int32
  }
  return Math.abs(h);
};

/** Map user.motivation to one of three template buckets. */
const motivationBucket = (user) => {
  const m = get(user, 'motivation', '');
  if (m === 'growth') return 'growth';
  if (m === 'succession') return 'succession';
  return 'exploring';
};

/**
 * Which variants (1..5) per bucket require which fields. Variants whose
 * required fields are blank get filtered out before the deterministic
 * index is picked, so we never render a template with a missing token.
 */
const VARIANT_REQUIREMENTS = {
  growth:     [[], [], [], ['years_practicing'], ['product_name']],
  succession: [[], [], ['years_practicing'], [], ['product_name']],
  exploring:  [[], [], [], ['years_practicing'], []],
};

/**
 * Build the interpolation payload with graceful fallbacks so templates
 * always render a clean sentence, even when data is thin.
 */
const interpolationValuesFor = (user, companyHasCounty, countryForCompany, agentWord) => {
  const usesCounty = countryForCompany === 'us' && companyHasCounty;
  const cityCandidate = usesCounty && user.county ? `${user.county} County` : (user.city || user.province);
  const productCandidate = get(majorityProductLine(user), '[0].name', '') || '';
  const yearsCandidate = Number(user.years_practicing) > 0 ? user.years_practicing : '';

  return {
    user_city: cityCandidate || 'the area',
    agent_word: agentWord || 'advisor',
    years_practicing: yearsCandidate,
    product_name: productCandidate,
  };
};

/**
 * Deterministic, i18n-aware headline generator.
 *
 * Flow:
 *   1. Pick a bucket based on user.motivation (growth / succession / exploring).
 *   2. Filter the bucket's 5 variants to those whose required fields are present.
 *   3. Hash the user identifier to pick a stable index into the remaining list.
 *   4. Look up the template via `intl.formatMessage` with the new key pattern
 *      `sign_up_pages.headline.<bucket>.<one|two|three|four|five>`.
 *
 * `intl` here can be a real `react-intl` instance OR the `placeholderIntl`
 * object that `HeadlineAndAboutMeStep.jsx` builds — both expose
 * `formatMessage({ id }, values)` and route the id to the component's local
 * translation table.
 */
export const generatedHeadlineForUser = (user, intl, companyHasCounty, countryForCompany, agentWord) => {
  if (!user) return '';

  const bucket = motivationBucket(user);
  const values = interpolationValuesFor(user, companyHasCounty, countryForCompany, agentWord);

  const variantNames = ['one', 'two', 'three', 'four', 'five'];
  const requirements = VARIANT_REQUIREMENTS[bucket] || VARIANT_REQUIREMENTS.exploring;

  const isRenderable = (reqs) => reqs.every((field) => values[field] !== '' && values[field] != null);
  const available = variantNames.filter((_, i) => isRenderable(requirements[i]));
  const pool = available.length > 0 ? available : variantNames.slice(0, 3); // safest fallbacks

  const idHash = stableHash(user.id || user.user_name || user.email || 'anon');
  const pick = pool[idHash % pool.length];

  const messageId = `sign_up_pages.headline.${bucket}.${pick}`;
  try {
    return intl.formatMessage({ id: messageId }, values);
  } catch (e) {
    // Belt-and-braces: if an id is missing in the local table, try the first
    // variant; if even that fails, synthesize a plain-language default so the
    // UI never renders the raw message id.
    try {
      return intl.formatMessage({ id: `sign_up_pages.headline.${bucket}.one` }, values);
    } catch (_) {
      return `Experienced ${values.agent_word} in ${values.user_city}`;
    }
  }
};

// Kept as a named re-export so any lingering importers don't crash. Deprecated:
// the generic `generatedHeadlineForUser` now handles all tenants (including
// wealth_rj) via the bucket/variant system. Delete once call sites are gone.
export const generatedRjHeadlineForUser = generatedHeadlineForUser;

export const generatedBioForUser = user => '';

export const areUsersTheSame = (user1, user2) => user1.user_name === user2.user_name;

export const interest_targets = user => user.transition_goals.map(t => snakeCase(lowerCase(t)));

//inputs: currentUser, and user 
//outputs: true if currentUser is allowed to view user's profile

export function canViewProfile(currentUser, user, company) {
  const viewingProfilesMotivation = get(currentUser, 'viewing_profiles_motivation');
  const companyBusinessType = get(company, 'business_type');
  const hasCommonDealPastOutForSignature = get(currentUser, 'is_in_deal_past_out_for_receiving_signature_state', false);
  const partialSaleVisibilityOptIn = get(currentUser, 'partial_sale_visibility_opt_in', false);
  if (
    companyBusinessType === 'wealth_rj' &&
    viewingProfilesMotivation === 'succession' &&
    (!hasCommonDealPastOutForSignature || !partialSaleVisibilityOptIn)
  ) {
    return false;
  }
  return true;
}

//inputs: currentUser, and user 
//outputs: true if currentUser can message user
export const canMessageUser = (currentUser, user, company) => {
  const companyBusinessType = get(company, 'business_type', '');
  const companyHasNewMessages = get(company, 'feature_types', []).includes('new_messages');
  const companyHasNetworking = get(company, 'feature_types', []).includes('networking');
  const companyAllowAllUsersToMessageEachOther = get(currentUser, 'company.feature_types', []).includes('allow_all_users_to_message_each_other');
  const currentUserIsSubscribed = get(currentUser, 'subscribed_to_marketplace', false);
  const prohibitedProfileTypesPandC = [ 'implicitly_obfuscated_user', 'explicitly_obfuscated_user', 'limited_access_seller', 'limited_access_buyer' ];
  const prohibitedProfileTypes = [ 'implicitly_obfuscated_user', 'explicitly_obfuscated_user', 'limited_access_seller', 'limited_access_buyer' ];
  const currentUserMotivation = get(currentUser, 'motivation');
  const currentUserPlanningPreference = get(currentUser, 'planning_preference');
  const userMotivation = get(user, 'motivation');
  const userPlanningPreference = get(user, 'planning_preference');
  const currentUserHasPlanningPreference = currentUserPlanningPreference === 'find_partner' || currentUserPlanningPreference === 'become_partner';
  const userHasPlanningPreference = userPlanningPreference === 'find_partner' || userPlanningPreference === 'become_partner';
  const currentUserIsSeller = currentUser.profile_type === 'limited_access_seller' || currentUser.profile_type === 'regular_buyer';
  const userIsSeller = user.profile_type === 'regular_buyer';
  const userIsExternalTalent = currentUser.talent_acquisition_user === true;

  if (companyBusinessType === 'p_and_c') {
    if( (companyHasNewMessages &&
      companyHasNetworking &&
      !prohibitedProfileTypesPandC.includes( currentUser.profile_type ) )) {
      return true;
    }else {
      return false;
    }
  }

  if (
      !userIsExternalTalent &&
       ((companyHasNewMessages &&
       companyHasNetworking &&
       !prohibitedProfileTypes.includes( currentUser.profile_type ) || 
    ((currentUserIsSeller) && userIsSeller)) &&
    ((currentUserMotivation === 'growth' && userMotivation === 'growth') ||
    (currentUserMotivation === 'growth' && userMotivation === 'no_motivation' && userHasPlanningPreference) ||
    (currentUserMotivation === 'no_motivation' && currentUserHasPlanningPreference && userMotivation === 'growth' && userHasPlanningPreference) ||
    (currentUserMotivation === 'no_motivation' && currentUserHasPlanningPreference && userMotivation === 'no_motivation' && userHasPlanningPreference))) ||
    (companyAllowAllUsersToMessageEachOther && currentUserIsSubscribed)
  ) {
    return true;
  }
  return false;
};